import type { ErrorEvent, EventHint } from "@similarweb/sentry-logger";

import {
    isForbiddenSalesWorkspaceListMessage,
    isForbiddenSalesWorkspaceListResponseError,
} from "app/pages/sales-intelligence/utils/errors/forbiddenSalesWorkspaceList";
import { ABORTED_CODE, ABORTED_NAME, ResponseError } from "app/services/fetchService";

import {
    getException,
    isAutomationIntegrationEvent,
    isI18nInitAutomationNoise,
    isResourceFetchAutomationNoise,
} from "./sentryEventResolvers";

const ANONYMOUS_CODE_ERROR_TEXT = "<anonymous code>";

// Browser-synthetic frame markers — not attributable to any source file (ours or otherwise).
const SYNTHETIC_FRAME_FILENAMES = new Set([ANONYMOUS_CODE_ERROR_TEXT, "<anonymous>", "[native code]"]);

const IGNORE_THIRD_PARTY = [
    "/s/fs.js",
    "chrome-extension://",
    "fast.appcues.com",
    "script.leadboxer.com",
    "/app/latest/cello.js",
    "@sentry-internal/rrweb",
    "js.knock-ai.com",
];
const ERROR_TYPES_BLOCK_LIST = ["AbortError", "Invalid small site", "API calls quota exceeded!"];
// Exact matches only — unlike ERROR_TYPES_BLOCK_LIST above, these aren't safe to substring-match.
const ERROR_TYPES_EXACT_BLOCK_LIST = new Set([JSON.stringify("Invalid site")]);

const KNOCK_AI_FETCH_FAILURE_PATTERN = /failed to fetch.*knock-ai\.com/i;

const ERROR_VALUES_BLOCK_LIST = [
    "Cannot read properties of null (reading 'ip')",
    "Object captured as promise rejection with keys: isCanceled",
    "Non-Error promise rejection captured with value: delay_connect",
    "Non-Error promise rejection captured with value: unauthorized",
    "Non-Error promise rejection captured with value: remote",
    "Uncaught InvalidStateError: Failed to execute 'send' on 'WebSocket': Still in CONNECTING state.",
    "Uncaught TypeError: Cannot read properties of undefined (reading 'send')",
    "Event `CustomEvent` (type=unhandledrejection) captured as promise rejection",
    "Could not establish connection. Receiving end does not exist.",
    "Failed to execute 'transaction' on 'IDBDatabase': One of the specified object stores was not found.",
    "Non-Error promise rejection captured with value: Handshake Timeout Reached",
    "API calls quota exceeded!",
    "Invalid call to runtime.sendMessage(). Tab not found.",
];

// Blocking specific errors of 403 caused by Cloudfront blocking the user request, as it's by design
const BLOCKED_ERROR_PATTERN = /403\s+could\s+not\s+satisfied\s+blockedWe/i;

const ERROR_VALUES_BLOCK_LIST_MUST_INVESTIGATE = [
    "Cannot read properties of undefined (reading 'close')",
    "undefined is not an object (evaluating 't.close')",
    "Cannot read property 'close' of undefined",
];
const ERROR_VALUE_BLOCK_LIST_AGGREGATED = new Set([
    ...ERROR_VALUES_BLOCK_LIST,
    ...ERROR_VALUES_BLOCK_LIST_MUST_INVESTIGATE,
]);

/**
 * Filters out Sales Intelligence specific Sentry errors.
 *
 * @returns null to drop the event, or the event if no filtering applies.
 */
const filterSiErrors = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
    const { tags = {}, exception } = event;

    if (tags.solution !== "sales-intelligence" || !exception) {
        return event;
    }

    const originalException = hint?.originalException;

    if (tags.sandboxName && tags.component === "userActivityService") {
        if (originalException instanceof ResponseError && originalException?.status === 404) {
            return null;
        }
    }

    if (isForbiddenSalesWorkspaceListResponseError(originalException)) {
        return null;
    }

    const exceptionValue = exception.values?.[0]?.value;
    if (isForbiddenSalesWorkspaceListMessage(exceptionValue)) {
        return null;
    }

    return event;
};

const thirdPartyFilenameRegex = new RegExp(IGNORE_THIRD_PARTY.join("|"));

const isThirdPartyError = (event: ErrorEvent | null): boolean => {
    const exception = getException(event);

    if (!exception) {
        return false;
    }

    return !!exception.stacktrace?.frames?.some((frame) =>
        frame.filename ? thirdPartyFilenameRegex.test(frame.filename) : false,
    );
};

const isAnonymousCodeError = (event: ErrorEvent | null): boolean => {
    const exception = getException(event);
    const frames = exception?.stacktrace?.frames;

    if (!frames?.length) {
        return false;
    }

    return frames.every((frame) => frame.filename === ANONYMOUS_CODE_ERROR_TEXT);
};

const hasNoAttributableFrame = (event: ErrorEvent | null): boolean => {
    const frames = getException(event)?.stacktrace?.frames;

    if (!frames?.length) {
        return true;
    }

    return frames.every((frame) => frame.filename == null || SYNTHETIC_FRAME_FILENAMES.has(frame.filename));
};

const isUnattributableNotAllowedError = (event: ErrorEvent | null): boolean =>
    getException(event)?.type === "NotAllowedError" && hasNoAttributableFrame(event);

const isIgnoredErrorType = (event: ErrorEvent | null): boolean => {
    const exception = getException(event);

    if (!exception) {
        return false;
    }

    if (ERROR_TYPES_EXACT_BLOCK_LIST.has(exception.type ?? "")) {
        return true;
    }

    return ERROR_TYPES_BLOCK_LIST.some((errorType) => exception.type?.includes(errorType));
};

const isIgnoredErrorValue = (event: ErrorEvent | null): boolean => {
    const exception = getException(event);

    if (!exception) {
        return false;
    }

    if (ERROR_VALUE_BLOCK_LIST_AGGREGATED.has(exception.value ?? "")) {
        return true;
    }

    return KNOCK_AI_FETCH_FAILURE_PATTERN.test(exception.value ?? "");
};

const RATE_LIMIT_STATUS_PATTERN = /status:\s*429\b/;

const isRateLimitError = (event: ErrorEvent | null, hint: EventHint): boolean => {
    const originalException = hint?.originalException;

    if (originalException && typeof originalException === "object") {
        if (originalException instanceof ResponseError && originalException.status === 429) {
            return true;
        }

        const cause = "cause" in originalException ? (originalException as Error).cause : undefined;
        if (cause instanceof ResponseError && cause.status === 429) {
            return true;
        }
    }

    const exception = getException(event);
    if (exception?.value && RATE_LIMIT_STATUS_PATTERN.test(exception.value)) {
        return true;
    }

    return false;
};

const isCloudfrontBlockedError = (event: ErrorEvent | null): boolean => {
    const exception = getException(event);

    if (!exception) {
        return false;
    }

    return BLOCKED_ERROR_PATTERN.test(exception.type ?? "");
};

const FAILED_TO_FETCH_PATTERN = /failed to fetch/i;
const BROWSER_FAILED_TO_FETCH_MESSAGE = "Failed to fetch";
const REQUEST_FAILED_STATUS_PATTERN = /Request failed with status:\s*(\d+)/;

const includesFailedToFetch = (value: string | undefined): boolean => FAILED_TO_FETCH_PATTERN.test(value ?? "");

const isCanonicalBrowserFailedToFetch = (type: string | undefined, value: string | undefined): boolean =>
    type === "TypeError" && value === BROWSER_FAILED_TO_FETCH_MESSAGE;

const isAbortedResponseError = (error: unknown): error is ResponseError =>
    error instanceof ResponseError && (error.status === ABORTED_CODE || error.name === ABORTED_NAME);

const parseStatusFromMessage = (message: string | undefined): number | undefined => {
    const match = REQUEST_FAILED_STATUS_PATTERN.exec(message ?? "");

    return match ? Number(match[1]) : undefined;
};

const isSuccessStatus = (status: number | undefined): boolean => status !== undefined && status >= 200 && status < 300;

// Success/aborted status proves it's noise; an unresolvable status is still noise if it's automation
// traffic (JN7C/JN7B/HATJ/HA0H/KEB3).
const isSuccessOrAutomationNoiseStatus = (status: number | undefined, event: ErrorEvent | null): boolean =>
    isSuccessStatus(status) || (status === undefined && isAutomationIntegrationEvent(event));

const getHttpStatusFromFetchEvent = (event: ErrorEvent | null, hint: EventHint): number | undefined => {
    const originalException = hint?.originalException;

    if (originalException instanceof ResponseError) {
        return originalException.status;
    }

    if (originalException instanceof Error && originalException.cause instanceof ResponseError) {
        return originalException.cause.status;
    }

    const responseErrorDetails = event?.extra?.responseErrorDetails;
    if (typeof responseErrorDetails === "object" && responseErrorDetails !== null && "status" in responseErrorDetails) {
        const status = (responseErrorDetails as { status: unknown }).status;
        if (typeof status === "number" && Number.isFinite(status)) {
            return status;
        }
    }

    const originalRaisedError = event?.extra?.originalRaisedError;
    if (typeof originalRaisedError === "object" && originalRaisedError !== null && "message" in originalRaisedError) {
        const status = parseStatusFromMessage((originalRaisedError as { message: string }).message);
        if (status !== undefined) {
            return status;
        }
    }

    return parseStatusFromMessage(getException(event)?.value);
};

const isClientNetworkFetchNoise = (event: ErrorEvent | null, hint: EventHint): boolean => {
    const originalException = hint?.originalException;

    if (isAbortedResponseError(originalException)) {
        return true;
    }

    if (originalException instanceof Error && isAbortedResponseError(originalException.cause)) {
        return true;
    }

    const exception = getException(event);

    if (isCanonicalBrowserFailedToFetch(exception?.type, exception?.value)) {
        return true;
    }

    if (originalException instanceof Error) {
        if (isCanonicalBrowserFailedToFetch(originalException.name, originalException.message)) {
            return true;
        }
    }

    const status = getHttpStatusFromFetchEvent(event, hint);

    if (includesFailedToFetch(exception?.type)) {
        return isSuccessOrAutomationNoiseStatus(status, event);
    }

    if (originalException instanceof ResponseError && includesFailedToFetch(originalException.name)) {
        return isSuccessOrAutomationNoiseStatus(status, event);
    }

    if (
        originalException instanceof Error &&
        originalException.cause instanceof ResponseError &&
        includesFailedToFetch(originalException.cause.name)
    ) {
        return isSuccessOrAutomationNoiseStatus(status, event);
    }

    return false;
};

export const filterSentryErrorEvents = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
    const siFilteredEvent = filterSiErrors(event, hint);

    if (
        siFilteredEvent === null ||
        isThirdPartyError(siFilteredEvent) ||
        isAnonymousCodeError(siFilteredEvent) ||
        isUnattributableNotAllowedError(siFilteredEvent) ||
        isIgnoredErrorType(siFilteredEvent) ||
        isIgnoredErrorValue(siFilteredEvent) ||
        isCloudfrontBlockedError(siFilteredEvent) ||
        isRateLimitError(siFilteredEvent, hint) ||
        isClientNetworkFetchNoise(siFilteredEvent, hint) ||
        isI18nInitAutomationNoise(siFilteredEvent) ||
        isResourceFetchAutomationNoise(siFilteredEvent)
    ) {
        return null;
    }

    return event;
};
